import type { AnyAction } from 'redux';

import type { UserOverridesItemRequest } from 'js/src/client';
import {
    clearAssetTimingOverrides,
    hydrateOverrides,
    setAssetTimingOverride,
    setBackgroundColorUserOverride,
    setBrightnessUserOverride,
    setCropUserOverride,
    setKenburnUserOverride,
    setSceneDurationUserOverride,
    setSceneOverrides,
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides/user-overrides-actions';
import type { UserOverrides } from 'js/src/data/user-overrides/user-overrides-types';
import { DEFAULT_USER_OVERRIDES } from 'js/src/data/user-overrides/user-overrides-types';

function _slide_user_overrides(
    state: UserOverridesItemRequest = {
        duration: DEFAULT_USER_OVERRIDES.DURATION,
        text_position: DEFAULT_USER_OVERRIDES.TEXT_POSITION,
        text_size: DEFAULT_USER_OVERRIDES.TEXT_SIZE,
        text_alignment: DEFAULT_USER_OVERRIDES.TEXT_ALIGNMENT,
        background_transform: DEFAULT_USER_OVERRIDES.BACKGROUND_TRANSFORM,
        background_animation: DEFAULT_USER_OVERRIDES.BACKGROUND_ANIMATION,
        brightness: DEFAULT_USER_OVERRIDES.BRIGHTNESS,
        background_color: DEFAULT_USER_OVERRIDES.BACKGROUND_COLOR,
        asset_timing_overrides: [],
    },
    action: AnyAction,
) {
    if (setTextPositionUserOverride.match(action)) {
        return Object.assign({}, state, {
            text_position: !action.payload.isAutomatic,
        });
    }
    if (setTextSizeUserOverride.match(action)) {
        return Object.assign({}, state, {
            text_size: !action.payload.isAutomatic,
        });
    }
    if (setTextAlignmentUserOverride.match(action)) {
        return Object.assign({}, state, {
            text_alignment: !action.payload.isAutomatic,
        });
    }
    if (setSceneDurationUserOverride.match(action)) {
        return Object.assign({}, state, {
            duration: !action.payload.isAutomatic,
        });
    }
    if (setCropUserOverride.match(action)) {
        return Object.assign({}, state, {
            background_transform: !action.payload.isAutomatic,
        });
    }
    if (setKenburnUserOverride.match(action)) {
        return Object.assign({}, state, {
            background_animation: !action.payload.isAutomatic,
        });
    }
    if (setBrightnessUserOverride.match(action)) {
        return Object.assign({}, state, {
            brightness: !action.payload.isAutomatic,
        });
    }
    if (setBackgroundColorUserOverride.match(action)) {
        return Object.assign({}, state, {
            background_color: !action.payload.isAutomatic,
        });
    }
    if (setAssetTimingOverride.match(action)) {
        const currentOverrides = state.asset_timing_overrides || [];
        let newOverrides: string[];
        if (action.payload.isOverridden) {
            // Add asset ID if not already present
            newOverrides = currentOverrides.includes(action.payload.assetId)
                ? currentOverrides
                : [...currentOverrides, action.payload.assetId];
        } else {
            // Remove asset ID
            newOverrides = currentOverrides.filter((id) => id !== action.payload.assetId);
        }
        return Object.assign({}, state, {
            asset_timing_overrides: newOverrides,
        });
    }
    return state;
}

export function user_overrides(state: UserOverrides = {}, action: AnyAction) {
    if (hydrateOverrides.match(action)) {
        return Object.assign({}, state, action.payload.overrides);
    }
    if (
        setTextPositionUserOverride.match(action) ||
        setTextSizeUserOverride.match(action) ||
        setTextAlignmentUserOverride.match(action) ||
        setSceneDurationUserOverride.match(action) ||
        setCropUserOverride.match(action) ||
        setKenburnUserOverride.match(action) ||
        setBrightnessUserOverride.match(action) ||
        setBackgroundColorUserOverride.match(action) ||
        setAssetTimingOverride.match(action)
    ) {
        return Object.assign({}, state, {
            [action.payload.sceneId]: _slide_user_overrides(state[action.payload.sceneId], action),
        });
    }
    if (setSceneOverrides.match(action)) {
        return Object.assign({}, state, {
            [action.payload.sceneId]: action.payload.overrides,
        });
    }
    if (clearAssetTimingOverrides.match(action)) {
        const newState: UserOverrides = {};
        Object.keys(state).forEach((sceneId) => {
            newState[sceneId] = Object.assign({}, state[sceneId], {
                asset_timing_overrides: [],
            });
        });
        return newState;
    }
    return state;
}
